import React, { useEffect, useState, useContext, useRef } from 'react'
import CartContext from '../Context/CartContext';
import { Input, Segment } from 'semantic-ui-react';

import * as styles from './styles/addToCart.module.css'
import Negosyo from '../../../lib/negosyo';

const AddToCartListing = ({
    disabled,
    product // {id, name, price, sku, image}
}) => {
    const {
        updateCartCount,
        gettingCartId,
        initiateRetry,

        showPrompt,
        addToCartDisabled,
        handleAddToCartDisable
    } = useContext(CartContext);
    const timeoutRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [hasError, setHasError] = useState(false);

    // Clear any timeout on umnount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        }
    }, [])

    useEffect(() => {
        setQuantity(disabled ? 0 : 1)
    }, [disabled]);

    const validate = (value) => {
        const digitRegex = /^[0-9]*$/

        // If value contain other characters other than digits
        if (!digitRegex.test(value)) return false;

        // If the value is 0
        if (parseInt(value, 10) === 0) return false;

        return true;
    }

    const handleInputChange = (e) => {
        if (validate(e.target.value)) {
            setHasError(false);
            setQuantity(e.target.value);
        }
    }

    const handleSubmit = async (cartId) => {
        try {
            setLoading(true);

            const { id, name, image } = product;

            if (id === undefined || id === null) {
                throw new Error("No product id");
            }

            const response = await Negosyo.addToCart(cartId, id, quantity);

            if (!response.hasOwnProperty("data")) {
                setHasError(true);
                setLoading(false);
                handleAddToCartDisable(false);
                return;
            }

            const { data } = response;

            // Total cart quantity
            let total = 0;
            // Add up all the quantity of the products in cart
            for (let i = 0; i < data.length; i++) {
                total += data[i].data.reduce((a, c) => a + c.quantity, 0)
            }

            updateCartCount(total, cartId);

            setLoading(false);
            handleAddToCartDisable(false);
            showPrompt(name, image, quantity);
            addDataLayer();
        } catch (error) {
            setHasError(true);
            setLoading(false);
            handleAddToCartDisable(false);
        }
    }

    const retryCartId = () => {
        let cartId = localStorage.getItem("mcart");

        if (cartId) {
            handleSubmit(cartId);
            return;
        } else {
            // If there is still no cart id after trying to get a new one
            if (gettingCartId === false && localStorage.getItem("mcart") === null) {
                setHasError(true);
                setLoading(false);
                handleAddToCartDisable(false);
                return;
            } else {
                timeoutRef.current = setTimeout(retryCartId, 100);
            }
        }
    }

    const handleClick = async (e) => {
        // To prevent Product Card events to be triggered
        e.stopPropagation();
        e.preventDefault();

        if (!validate(quantity) || quantity === "") {
            setHasError(true);
            return;
        }

        setHasError(false);
        setLoading(true)
        handleAddToCartDisable(true);

        let cartId = localStorage.getItem("mcart");

        if (cartId) {
            handleSubmit(cartId);
            return;
        } else {
            // CartProvider is not trying to get a new cart id
            if (gettingCartId === false) {
                const newCartId = localStorage.getItem("mcart");

                // If there is no cart id in localStorage, try to get a new cart id
                if (!newCartId) {
                    initiateRetry().then(retryCartId)
                } else {
                    handleSubmit(newCartId);
                }
            } else {
                // CartProvider is already trying to get a new cart id
                timeoutRef.current = setTimeout(() => handleClick(e), 200)
            }
        }
    }

    const addDataLayer = () => {
        if (product !== null) {
            const { name, price, sku } = product;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'addToCart',
                'ecommerce': {
                    'add': {
                        'value': parseFloat(price) * parseInt(quantity, 10),
                        'products': [
                            {
                                'name': name,
                                'price': price,
                                'id': sku,
                                'quantity': parseInt(quantity, 10),
                                'item_id': sku,
                                'item_name': name,
                                'currency': "PHP",
                            }
                        ]
                    }
                }
            })
        }
    }

    return (
        <>
            <Segment
                basic
                className={`${styles.addToCartListingContainer}`}
                onClick={(e) => {
                    // To prevent Product Card events to be triggered
                    e.stopPropagation();
                    e.preventDefault();
                }}
                onMouseDown={(e) => {
                    // To prevent Product Card events to be triggered
                    e.stopPropagation();
                }}
            >
                <Input
                    size='mini'
                    className={`${styles.addToCartListing}`}
                    name="addToCartQuantity"
                    placeholder="1"
                    value={quantity}
                    error={hasError}
                    onChange={handleInputChange}
                    disabled={loading || disabled || addToCartDisabled}
                    onClick={(e) => {
                        // To prevent Product Card events to be triggered
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                        // To prevent Product Card events to be triggered
                        e.stopPropagation();
                    }}
                    action={{
                        size: 'tiny',
                        className: `${styles.addButtons}`,
                        content: loading ? "Loading..." : "Add to Cart",
                        loading: loading,
                        disabled: loading || disabled || addToCartDisabled,
                        name: "addToCartButton",
                        onClick: handleClick
                    }}
                />
            </Segment>
        </>
    )
}

export default AddToCartListing